'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Loader2Icon } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import axios from 'axios';

import { Button } from '@/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/ui/form';
import { Input } from '@/ui/input';
import { Textarea } from '@/ui/textarea';
import { VolunteerFormFields } from '@/types/constants';
import { Separator } from '@/ui/separator';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTrigger } from '@/ui/dialog';
import GreenCheckIcon from '@/icons/green-check.svg';
import { cn } from '@/lib/utils';
import { postVolunteerResponse } from '@/api/user';
import { toast, useToast } from '@/ui/use-toast';
import { useTracker } from '@/lib/tracking/useTracker';

const FormSchema = z.object({
	[VolunteerFormFields.NAME]: z.string().min(2, {
		message: 'Name must be at least 2 characters',
	}),
	[VolunteerFormFields.MOBILE]: z.string().regex(/^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/, {
		message: 'Please enter a valid mobile number',
	}),
	[VolunteerFormFields.EMAIL]: z.string().email({
		message: 'Please enter a valid email address',
	}),
	[VolunteerFormFields.ADDRESS_LINE_1]: z.string().min(2, {
		message: 'Address must be at least 2 characters',
	}),
	[VolunteerFormFields.ADDRESS_LINE_2]: z.string().min(2, {
		message: 'Address must be at least 2 characters',
	}),
	[VolunteerFormFields.CITY]: z.string().min(2, {
		message: 'City must be at least 2 characters',
	}),
	[VolunteerFormFields.STATE]: z.string().min(2, {
		message: 'State must be at least 2 characters',
	}),
	[VolunteerFormFields.PINCODE]: z.coerce.string().refine(
		value =>
			// add length check with valid number regex
			/^\d{6}$/.test(value),
		{
			message: 'Please enter a valid pincode',
		},
	),
	[VolunteerFormFields.LOCATION_LINK]: z.union([
		z.literal(''),
		z.string().trim().url({
			message: 'Please enter a valid URL',
		}),
	]),
	[VolunteerFormFields.MESSAGE]: z
		.string()
		.min(10, {
			message: 'Message must be at least 10 characters',
		})
		.max(255, {
			message: 'Message must be at most 255 characters',
		}),
});

const defaultValues = {
	[VolunteerFormFields.NAME]: '',
	[VolunteerFormFields.MOBILE]: '',
	[VolunteerFormFields.EMAIL]: '',
	[VolunteerFormFields.ADDRESS_LINE_1]: '',
	[VolunteerFormFields.ADDRESS_LINE_2]: '',
	[VolunteerFormFields.CITY]: '',
	[VolunteerFormFields.STATE]: '',
	[VolunteerFormFields.PINCODE]: '',
	[VolunteerFormFields.LOCATION_LINK]: '',
	[VolunteerFormFields.MESSAGE]: '',
};

export function VolunteerForm() {
	const { toast } = useToast();
	const { trackEvent } = useTracker();
	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
		defaultValues: defaultValues,
		mode: 'onBlur',
	});

	async function onSubmit(data: z.infer<typeof FormSchema>) {
		try {
			// DEV: Transform the mobile number to the required format
			data[VolunteerFormFields.MOBILE] = `+91${data[VolunteerFormFields.MOBILE]}`;
			await postVolunteerResponse(data);
			setIsSuccessDialogOpen(true);
			form.reset(defaultValues);

			trackEvent({
				event_type: 'RECEIVE',
				component: 'SEND_MESSAGE',
				ui_source: 'VOLUNTEER_FORM',
				meta_data: {
					form_data: data,
				},
			});
		} catch (error) {
			trackEvent({
				event_type: 'ERROR_BOUNDARY',
				component: 'SEND_MESSAGE',
				ui_source: 'VOLUNTEER_FORM',
				meta_data: {
					error: error,
					form_data: data,
				},
			});

			if (axios.isAxiosError(error)) {
				toast({
					variant: 'destructive',
					title: 'An unexpected error has occurred while submitting the form. Please try again later.',
					description: error.response?.data?.message ?? error.message,
				});
			}
		}
	}

	const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
	const { isSubmitting, isValid } = form.formState;

	return (
		<div className="my-4 flex flex-col gap-6">
			<h3 className="text-2xl font-semibold text-primary md:text-3xl">Enter your details</h3>
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 rounded-lg text-base">
					<div className="grid gap-4 sm:grid-cols-2">
						<FormField
							control={form.control}
							name={VolunteerFormFields.NAME}
							render={({ field }) => (
								<FormItem className="col-span-2 sm:col-span-1">
									<FormControl>
										<Input
											placeholder="Your name*"
											className={cn('h-12 text-base font-normal', {
												'border-2 border-red-500': form.formState.errors[field.name],
											})}
											{...field}
											disabled={isSubmitting}
										/>
									</FormControl>
									<FormMessage className="mt-1 text-xs" />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name={VolunteerFormFields.MOBILE}
							render={({ field }) => (
								<FormItem className="col-span-2 sm:col-span-1">
									<FormControl>
										<div
											className={cn(
												'flex h-12 items-center justify-center gap-2 rounded-md border border-input bg-transparent pl-3 text-base font-normal focus-within:ring-2 focus-within:ring-ring  focus-within:ring-offset-2 focus-visible:outline-none',
												{
													'border-2 border-red-500': form.formState.errors[field.name],
												},
											)}
										>
											<span className={'text-muted-foreground'}>+91</span>
											<Separator orientation="vertical" className="h-[70%]" />
											<input
												placeholder="Enter mobile number*"
												{...field}
												type="tel"
												inputMode="numeric"
												onChange={e => {
													const value = e.target.value;
													if (!/^\d{0,10}$/.test(value)) {
														return;
													}
													field.onChange(e);
												}}
												disabled={isSubmitting}
												className={
													'flex size-full rounded-md border-none bg-transparent py-2 text-base shadow-none outline-none placeholder:text-muted-foreground focus-visible:border-none focus-visible:shadow-none focus-visible:outline-none'
												}
											/>
										</div>
									</FormControl>
									<FormMessage className="mt-1 text-xs" />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name={VolunteerFormFields.EMAIL}
							render={({ field }) => (
								<FormItem className="col-span-2">
									<FormControl>
										<Input
											placeholder="Enter your email*"
											className={cn('h-12 text-base font-normal', {
												'border-2 border-red-500': form.formState.errors[field.name],
											})}
											{...field}
											disabled={isSubmitting}
										/>
									</FormControl>{' '}
									<FormMessage className="mt-1 text-xs" />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name={VolunteerFormFields.ADDRESS_LINE_1}
							render={({ field }) => (
								<FormItem className="col-span-2">
									<FormControl>
										<Input
											placeholder="Flat / House no / Floor / Building*"
											className={cn('h-12 text-base font-normal', {
												'border-2 border-red-500': form.formState.errors[field.name],
											})}
											{...field}
											disabled={isSubmitting}
										/>
									</FormControl>
									<FormMessage className="mt-1 text-xs" />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name={VolunteerFormFields.ADDRESS_LINE_2}
							render={({ field }) => (
								<FormItem className="col-span-2">
									<FormControl>
										<Input
											placeholder="Area / Sector / Locality*"
											className={cn('h-12 text-base font-normal', {
												'border-2 border-red-500': form.formState.errors[field.name],
											})}
											{...field}
											disabled={isSubmitting}
										/>
									</FormControl>
									<FormMessage className="mt-1 text-xs" />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name={VolunteerFormFields.CITY}
							render={({ field }) => (
								<FormItem className="col-span-2 sm:col-span-1">
									<FormControl>
										<Input
											placeholder="City*"
											className={cn('h-12 text-base font-normal', {
												'border-2 border-red-500': form.formState.errors[field.name],
											})}
											{...field}
											disabled={isSubmitting}
										/>
									</FormControl>
									<FormMessage className="mt-1 text-xs" />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name={VolunteerFormFields.STATE}
							render={({ field }) => (
								<FormItem className="col-span-2 sm:col-span-1">
									<FormControl>
										<Input
											placeholder="State*"
											className={cn('h-12 text-base font-normal', {
												'border-2 border-red-500': form.formState.errors[field.name],
											})}
											{...field}
											disabled={isSubmitting}
										/>
									</FormControl>
									<FormMessage className="mt-1 text-xs" />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name={VolunteerFormFields.PINCODE}
							render={({ field }) => (
								<FormItem className="col-span-2 sm:col-span-1">
									<FormControl>
										<Input
											placeholder="Pin code*"
											{...field}
											type="tel"
											inputMode="numeric"
											onChange={e => {
												const value = e.target.value;
												if (!/^\d{0,6}$/.test(value)) {
													return;
												}
												field.onChange(e);
											}}
											className={cn('h-12 text-base font-normal', {
												'border-2 border-red-500': form.formState.errors[field.name],
											})}
											disabled={isSubmitting}
										/>
									</FormControl>
									<FormMessage className="mt-1 text-xs" />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name={VolunteerFormFields.LOCATION_LINK}
							render={({ field }) => (
								<FormItem className="col-span-2 sm:col-span-1">
									<FormControl>
										<Input
											placeholder="Google map link for location"
											className={cn('h-12 text-base font-normal', {
												'border-2 border-red-500': form.formState.errors[field.name],
											})}
											{...field}
											disabled={isSubmitting}
										/>
									</FormControl>
									<FormMessage className="mt-1 text-xs" />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name={VolunteerFormFields.MESSAGE}
							render={({ field }) => (
								<FormItem className="col-span-2">
									<FormControl>
										<Textarea
											placeholder="Write your message*"
											className={cn('min-h-32 resize-none text-base font-normal md:min-h-36 ', {
												'border-2 border-red-500': form.formState.errors[field.name],
											})}
											{...field}
											disabled={isSubmitting}
										/>
									</FormControl>
									<FormMessage className="mt-1 text-xs" />
								</FormItem>
							)}
						/>
					</div>
					<Dialog
						open={isSuccessDialogOpen}
						onOpenChange={isOpen => {
							if (!isOpen) {
								setIsSuccessDialogOpen(false);
							}
						}}
					>
						<DialogTrigger asChild>
							<Button
								variant={'zomato'}
								aria-label="Submit"
								type="submit"
								size="xl"
								onClick={() => {
									trackEvent({
										event_type: 'CLICK',
										component: 'SEND_MESSAGE',
										ui_source: 'VOLUNTEER_FORM',
									});
								}}
							>
								{isSubmitting && <Loader2Icon className="mr-2 animate-spin" />}
								Submit
							</Button>
						</DialogTrigger>
						<DialogContent className="flex max-w-xs flex-col items-center justify-center rounded-2xl sm:max-w-sm sm:gap-6">
							<DialogHeader>
								<Image
									src={GreenCheckIcon}
									alt="Green Check Icon"
									className="delay-100 animate-in fade-in-0 zoom-in"
								/>
							</DialogHeader>
							<div className="flex flex-col items-center justify-center gap-2 sm:gap-4">
								<h4 className="text-center text-sm font-normal text-zinc-500 md:text-base">
									Thanks for filling out the form, our team will get in touch with you soon
								</h4>
							</div>
							<DialogFooter>
								<DialogClose asChild>
									<Button variant="zomato" type="submit" className="w-48">
										Okay
									</Button>
								</DialogClose>
							</DialogFooter>
						</DialogContent>
					</Dialog>
				</form>
			</Form>
		</div>
	);
}
